import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Inspection } from '../types';
import { INSPECTION_STATUS_LABELS } from '../types';
import { useInspections } from '../hooks/useInspections';
import { useWebViewLoading } from '../../../shared/hooks';
import { isWebViewEnvironment, callFlutterHandler, postMessageToFlutter } from '../../../shared/services/bridge';
import { formatDateTimeMinute } from '../../../shared/utils/datetime';

import './InspectionList.css';

interface InspectionRowProps {
    inspection: Inspection;
}

const InspectionRow: React.FC<InspectionRowProps> = ({ inspection }) => {
    const navigate = useNavigate();

    const handleRowClick = () => {
        if (isWebViewEnvironment()) {
            // ローディング表示を先に開始（React側で制御）
            postMessageToFlutter('showLoading');
            callFlutterHandler('InspectionHandler', inspection.id.toString());
        } else {
            navigate(`/inspections/${inspection.id}`);
        }
    };

    return (
        <div className="inspection-row" onClick={handleRowClick}>
            <div className="cell status">{INSPECTION_STATUS_LABELS[inspection.status]}</div>
            <div className="cell request-date">{formatDateTimeMinute(inspection.requestDate)}</div>
            <div className="cell visit-date">{formatDateTimeMinute(inspection.visitDate)}</div>
            <div className="cell icon-cell">›</div>
        </div>
    );
};

function InspectionList() {
    const { ongoingInspections, completedInspections, isLoading } = useInspections();

    useEffect(() => {
        document.body.style.overflow = 'auto';
    }, []);

    // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
    // コールバックrefを使用してDOMが実際に描画された後にhideLoadingを呼ぶ
    const contentRef = useWebViewLoading(isLoading);

    // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
    if (isLoading) {
        return <div className="inspection-container" />;
    }

    return (
        <main ref={contentRef} className="inspection-container">
            <h1 className="main-title">点検お問い合わせ一覧</h1>
            <hr className="divider" />

            <section className="inspection-section">
                <h2 className="section-title ongoing">進行中のお問い合わせ内容</h2>
                <div className="inspection-table">
                    <div className="inspection-header">
                        <div className="cell status">ステータス</div>
                        <div className="cell request-date">依頼日時</div>
                        <div className="cell visit-date">訪問日時</div>
                        <div className="cell icon-cell"></div>
                    </div>
                    {ongoingInspections.map(item => <InspectionRow key={item.id} inspection={item} />)}
                </div>
            </section>

            <section className="inspection-section">
                <h2 className="section-title completed">完了したお問い合わせ内容</h2>
                <div className="inspection-table">
                    <div className="inspection-header">
                        <div className="cell status">ステータス</div>
                        <div className="cell request-date">依頼日時</div>
                        <div className="cell visit-date">訪問日時</div>
                        <div className="cell icon-cell"></div>
                    </div>
                    {completedInspections.map(item => <InspectionRow key={item.id} inspection={item} />)}
                </div>
            </section>
        </main>
    );
}

export default InspectionList;
